import React, { useEffect, useState } from "react";

import { Silence } from "@energy-advice/alertfan-api-client";
import { SilenceState } from "@energy-advice/alertfan-api-client/dist/lib/generated/api";
import { useDeleteSilence, useGetSilences } from "api/api-hooks/alert-fan-hooks";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import useSafeId from "hooks/dashboard/useDashboardComponentId";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable } from "primereact/datatable";
import { InputSwitch } from "primereact/inputswitch";
import { Tooltip } from "react-tooltip";
import { T } from "services/lang/LanguageService";
import { DweenTimeUnitEnum, fmtDateUntilUnit } from "utils/time-utils";
import { useTimezone } from "hooks/useTimezone";
import { copyToClipBoard } from "utils/clipboard";
import { stringArrayToTags } from "../../basic/strings-array-to-tags/StringArrayToTags";
import SilenceCreate from "./SilenceCreate";

const SilenceStateIconMap: Record<SilenceState, any> = {
  ACTIVE: <i className="pi pi-check-circle" style={{ color: "green" }} />,
  EXPIRED: <i className="pi pi-clock" style={{ color: "indianred" }} />,
  PENDING: <i className="pi pi-hourglass pi-spin" style={{ color: "darkorange" }} />,
};

const SilenceStateTextMap: Record<SilenceState, any> = {
  ACTIVE: "Active",
  EXPIRED: "Expired",
  PENDING: "Pending",
};

type SilencesType = {
  tags?: { topics?: Array<string>; items?: Array<string> };
  setTags: (tags: { topics?: Array<string>; items?: Array<string> }) => void;
};

const Silences = ({ tags, setTags }: SilencesType) => {
  const tz = useTimezone();
  const [addSilenceDialog, setAddSilenceDialog] = useState(false);
  const [showExpired, setShowExpired] = useState(false);

  const { data, error, refetch, isLoading } = useGetSilences(showExpired);
  const tooltipId = useSafeId();

  const { mutate } = useDeleteSilence(showExpired, {
    onSuccess: () => {
      easasAlert({
        summary: T("alert.success"),
        severity: "success",
        detail: T("alert.silence.silence-was-deleted"),
        life: 3000,
      });
    },
    onFailure: err => {
      easasAlert({ summary: T("alert.error"), severity: "error", detail: err, life: 3000 });
    },
  });

  useEffect(() => {
    if (tags && (tags.items || tags.topics)) {
      setAddSilenceDialog(true);
    }
  }, [tags]);

  const showDialogFalse = () => {
    setAddSilenceDialog(false);
    setTags({});
  };

  const deleteSilenceBody = (rowData: Silence) => {
    const acceptFunc = () => {
      mutate(rowData.id);
    };

    const confirm = (event: any) => {
      confirmPopup({
        target: event.currentTarget,
        message: T("alert.are-you-sure-you-want-to-proceed"),
        icon: "pi pi-exclamation-triangle",
        accept: () => acceptFunc(),
      });
    };

    return <Button className="component-editor-button icon-red" icon="pi pi-trash" onClick={confirm} />;
  };

  const idBody = (rowData: Silence) => {
    return (
      <>
        <div
          id={rowData.id}
          className="flex align-items-center justify-content-center"
          onClick={async () => {
            await copyToClipBoard(rowData.id);
            easasAlert({
              summary: T("alert.success"),
              severity: "success",
              detail: T("alert.id-copied-to-clipboard"),
            });
          }}
        >
          <i className="pi pi-info-circle" />
        </div>
        <Tooltip anchorSelect={`#${rowData.id}`} content={rowData.id} delayShow={300} />
      </>
    );
  };

  const stateBody = (rowData: Silence) => {
    return (
      <>
        <div id={tooltipId} className="flex align-items-center justify-content-center">
          {SilenceStateIconMap[rowData.state]}
        </div>
        <Tooltip anchorSelect={`#${tooltipId}`} content={SilenceStateTextMap[rowData.state]} delayShow={300} />
      </>
    );
  };

  if (error) {
    return <div>{T("error.something-went-wrong")}...</div>;
  }

  return (
    <div className="flex flex-column gap-2">
      <div className="flex gap-2 justify-content-between align-items-center">
        <Button
          icon="pi pi-plus"
          label={T("action.create")}
          className="component-editor-button icon-green"
          onClick={() => setAddSilenceDialog(true)}
        />
        <div className="flex gap-2 align-items-end">
          <div className="flex flex-column justify-content-center">
            <b>{T("action.show-expired")}</b>
            <div className="flex justify-content-center">
              <InputSwitch checked={showExpired} onChange={e => setShowExpired(e.value)} />
            </div>
          </div>
          <Button icon="pi pi-refresh" className="component-editor-button icon-blue" onClick={() => refetch()} />
        </div>
      </div>
      <DataTable value={data} loading={isLoading} size="small">
        <Column header="ID" field="id" body={idBody} style={{ width: "0" }} />
        <Column header={T("user.state")} field="state" style={{ width: "0", whiteSpace: "nowrap" }} body={stateBody} />
        <Column header={T("word.item-tags")} field="itemTags" body={(e: Silence) => stringArrayToTags(e.itemTags)} />
        <Column header={T("word.topic-tags")} field="topicTags" body={(e: Silence) => stringArrayToTags(e.topicTags)} />
        <Column
          header={T("word.started")}
          field="startsAt"
          style={{ width: "0", whiteSpace: "nowrap" }}
          body={(e: Silence) =>
            e.startsAt ? fmtDateUntilUnit(new Date(e.startsAt), DweenTimeUnitEnum.SECONDS, tz.display) : "-"
          }
        />
        <Column
          header={T("word.ends-at")}
          field="endsAt"
          style={{ width: "0", whiteSpace: "nowrap" }}
          body={(e: Silence) => (e.endsAt ? fmtDateUntilUnit(new Date(e.endsAt), DweenTimeUnitEnum.SECONDS, tz.display) : "-")}
        />
        <Column
          header={T("word.updated")}
          field="updatedAt"
          style={{ width: "0", whiteSpace: "nowrap" }}
          body={(e: Silence) =>
            e.updatedAt ? fmtDateUntilUnit(new Date(e.updatedAt), DweenTimeUnitEnum.SECONDS, tz.display) : "-"
          }
        />
        <Column header={T("word.creator")} field="createdBy" style={{ width: "0", whiteSpace: "nowrap" }} />
        <Column header={T("word.comment")} field="comment" />
        <Column body={deleteSilenceBody} style={{ width: "0" }} />
      </DataTable>
      {addSilenceDialog && (
        <SilenceCreate showExpired={showExpired} dialog={addSilenceDialog} showDialog={showDialogFalse} tags={tags} />
      )}
    </div>
  );
};

export default Silences;
